/**
 * Teacher Stats — Dashboard tab. Per-teacher engagement dashboard.
 * See docs/FeatureDesigns/teacher-stats.md.
 *
 * Self-contained: hosts its own teacher selector, range toggle, tenant-filter
 * coupling, and data fetching. Dashboard just renders <TeacherStatsTab />.
 *
 * "All teachers" mode: when the sidebar tenant filter is scoped to a single
 * tenant, the dropdown offers an "All teachers" entry that aggregates across
 * every student in the tenant (no teacher mapping required). Disabled when
 * the sidebar is on "All tenants" — server rejects the call without a
 * tenantId, and a cross-tenant aggregate would just duplicate the platform
 * Usage tab.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, LoadingSpinner } from '@shared-ui/components';
import { adminApi, ALL_TEACHERS, type TeacherStatsResponse } from '../../api/adminApi';
import { ALL_TENANTS, useSelectedTenantId } from '../../components/TenantFilter';
import { EngagementSection } from './EngagementSection';
import { RetentionSection } from './RetentionSection';
import { EngagementByActivitiesChart } from '../dashboard/EngagementByActivitiesChart';
import './TeacherStats.css';

type RangeDays = 7 | 28 | 56;

const RANGE_OPTIONS: { value: RangeDays; label: string }[] = [
    { value: 7, label: '7 Days' },
    { value: 28, label: '4 Weeks' },
    { value: 56, label: '8 Weeks' },
];

export function TeacherStatsTab({ includeAll }: { includeAll?: boolean } = {}) {
    const [teacherId, setTeacherId] = useState<string>(ALL_TEACHERS);
    const [rangeDays, setRangeDays] = useState<RangeDays>(7);
    const selectedTenantId = useSelectedTenantId();
    const tenantScope = selectedTenantId === ALL_TENANTS ? undefined : selectedTenantId;
    const allTeachersAvailable = !!tenantScope;

    const teachersQuery = useQuery({
        queryKey: ['teachers-list-for-stats', tenantScope ?? '__all__'],
        queryFn: () => adminApi.getTeachers(tenantScope),
    });

    const statsQuery = useQuery({
        queryKey: ['teacher-stats', teacherId, rangeDays, tenantScope ?? '', !!includeAll],
        queryFn: () => adminApi.getTeacherStats(teacherId, rangeDays, tenantScope, includeAll),
        enabled: !!teacherId && (teacherId !== ALL_TEACHERS || allTeachersAvailable),
    });

    const teachers = teachersQuery.data ?? [];

    // Reset the teacher selection if the tenant scope changes and the
    // current teacher is no longer in the filtered list. Stay on the
    // ALL_TEACHERS sentinel even when the tenant filter is on "All tenants" —
    // body shows a tenant-prompt state, and the user is one tenant pick away
    // from a working aggregate view.
    useEffect(() => {
        if (teacherId && teacherId !== ALL_TEACHERS && teachers.length > 0
            && !teachers.some(t => t.userId === teacherId)) {
            setTeacherId(ALL_TEACHERS);
        }
    }, [teachers, teacherId]);

    return (
        <>
            <div className="ts-controls">
                <Select
                    className="ts-teacher-select"
                    value={teacherId}
                    onChange={e => setTeacherId(e.target.value)}
                    disabled={teachersQuery.isLoading}
                >
                    <option value={ALL_TEACHERS} disabled={!allTeachersAvailable}>
                        {allTeachersAvailable
                            ? 'All teachers (tenant aggregate)'
                            : 'All teachers (pick a tenant first)'}
                    </option>
                    {teachers.map(t => (
                        <option key={t.userId} value={t.userId}>{t.name}</option>
                    ))}
                </Select>

                <div className="ts-range-toggle">
                    {RANGE_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            className={rangeDays === opt.value ? 'active' : ''}
                            onClick={() => setRangeDays(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <Body teacherId={teacherId} statsQuery={statsQuery} allTeachersAvailable={allTeachersAvailable} />
        </>
    );
}

interface BodyProps {
    teacherId: string;
    statsQuery: ReturnType<typeof useQuery<TeacherStatsResponse>>;
    allTeachersAvailable: boolean;
}

function Body({ teacherId, statsQuery, allTeachersAvailable }: BodyProps) {
    if (teacherId === ALL_TEACHERS && !allTeachersAvailable) {
        return <div className="ts-empty">Pick a tenant from the sidebar filter to see all-teacher stats.</div>;
    }
    if (!teacherId) {
        return <div className="ts-empty">Pick a teacher to see their student engagement stats.</div>;
    }
    if (statsQuery.isLoading) return <LoadingSpinner />;
    if (statsQuery.error) {
        return <div className="ts-empty">Failed to load: {(statsQuery.error as Error).message}</div>;
    }
    if (!statsQuery.data) return null;

    const stats = statsQuery.data;
    const isAggregate = teacherId === ALL_TEACHERS;
    return (
        <>
            <div className="stat-cards stat-cards--auto">
                <Card
                    value={stats.totalStudents}
                    label="Total students"
                    sub={isAggregate ? 'All students in tenant' : 'Active enrollments'}
                />
                <Card value={stats.visited7d} label="Visited" sub="last 7 days" />
                <Card value={stats.engaged7d} label="Engaged" sub="last 7 days" />
                <Card value={stats.practised7d} label="Practised" sub="last 7 days" />
                <Card value={stats.questionsAttempted7d} label="Questions attempted" sub="last 7 days" />
            </div>

            <EngagementSection stats={stats} />
            <EngagementByActivitiesChart series={stats.activityEngagement ?? []} weekly={stats.grain === 'weekly'} />
            <RetentionSection stats={stats} />
        </>
    );
}

interface CardProps { value: number; label: string; sub?: string }
function Card({ value, label, sub }: CardProps) {
    return (
        <div className="stat-card stat-card--fact">
            <div className="stat-card-value">{value}</div>
            <div className="stat-card-label">{label}</div>
            {sub && <div className="stat-card-comment">{sub}</div>}
        </div>
    );
}
